import { FC, useCallback, useMemo } from 'react';
import type {
  DateValidationError,
  PickerChangeHandlerContext
} from '@mui/x-date-pickers/models';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { AText } from '../AText';
import { Box } from '../Box';
import { AsyncDatePicker } from '../MUI/AsyncDatePicker';
import { formatToDateTime } from '../MUI/AsyncDatePicker/utils';
import { FONT_SIZE_INPUT, HEIGHT_INPUT } from './constants';

import styles from './index.module.css';

interface MaterialKeyboardDatePickerProps {
  name?: string;
  label?: string;
  minDateMessage?: string;
  maxDateMessage?: string;
  invalidDateMessage?: string;
  placeholder?: string;
  date?: Date | DateTime | string | null;
  onChange?: (
    date: Date | null,
    value?: PickerChangeHandlerContext<DateValidationError>
  ) => void;
  minDate?: Date | DateTime | string;
  maxDate?: Date | DateTime | string;
  format?: string;
  inputClassName?: string;
  className?: string;
  disabled?: boolean;
  labelClassName?: string;
  size?: 'xs' | 's' | 'm';
  labelSize?: 's' | 'm';
  shouldDisableDate?: (date: Date) => boolean;
  onError?: (error: string, value?: DateTime | null) => void;
  extraPadding?: boolean;
  error?: boolean;
  success?: boolean;
  focus?: boolean;
}

export const MaterialKeyboardDatePicker: FC<
  MaterialKeyboardDatePickerProps
> = ({
  name = '',
  label = '',
  minDateMessage = '',
  maxDateMessage = '',
  invalidDateMessage = '',
  placeholder = '',
  date = null,
  onChange = () => {},
  minDate = new Date('1900-01-01'),
  maxDate = new Date('2100-01-01'),
  format = 'yyyy-MM-dd',
  inputClassName = '',
  className = '',
  disabled = false,
  labelClassName = '',
  size = 's',
  labelSize = 'm',
  shouldDisableDate = () => false,
  onError = () => {},
  error,
  extraPadding,
  ...props
}) => {
  const isPlaceholder = placeholder && date === null;

  const inputStyles = useMemo(
    () => ({
      color: isPlaceholder
        ? 'var(--input_txt_placeholder)'
        : 'var(--input_txt)',
      fontSize: FONT_SIZE_INPUT[size] || FONT_SIZE_INPUT.s,
      height: HEIGHT_INPUT[size] || HEIGHT_INPUT.s,
      background: disabled
        ? 'var(--input_bg_color_2)'
        : 'var(--input_bg_color_1)',
      WebkitTextFillColor: isPlaceholder
        ? 'var(--input_txt_placeholder)'
        : 'var(--input_txt)',
      ...(extraPadding
        ? {
            paddingLeft: 'var(--b-spacing-m)',
            paddingRight: 'var(--b-spacing-m)'
          }
        : {})
    }),
    [size, isPlaceholder, disabled, extraPadding]
  );

  const onChangeHandler = useCallback(
    (
      d: DateTime<boolean> | null,
      e: PickerChangeHandlerContext<DateValidationError>
    ) => {
      if (e.validationError) {
        if (e.validationError === 'invalidDate' && invalidDateMessage) {
          onError(invalidDateMessage);
        }
        if (e.validationError === 'minDate' && minDateMessage) {
          onError(minDateMessage);
        }
        if (e.validationError === 'maxDate' && maxDateMessage) {
          onError(maxDateMessage);
        }

        if (typeof e === 'string') {
          onError(e);
        }
      } else {
        onError('');
      }

      onChange(d?.toJSDate() || null, e);
    },
    [invalidDateMessage, maxDateMessage, minDateMessage, onChange, onError]
  );

  return (
    <Box className={className} direction="column">
      {label && (
        <Box
          direction="column"
          bottom="s"
          className={clsx(styles.label, labelClassName)}
        >
          <AText size={labelSize} breed="R">
            {label}
          </AText>
        </Box>
      )}

      <Box className={`${styles.datePickerWrapper} ${styles[size]}`}>
        <AsyncDatePicker
          isKeyboard={true}
          name={name}
          isDialog={false}
          value={formatToDateTime(date) || null}
          onChange={onChangeHandler}
          format={format}
          minDate={formatToDateTime(minDate)}
          maxDate={formatToDateTime(maxDate)}
          placeholder={placeholder}
          slotProps={{
            textField: {
              error: !!error,
              variant: 'standard',
              sx: {
                width: '100%',

                '& .MuiInputBase-root': {
                  ...inputStyles
                },

                '& .MuiInputBase-root.Mui-error': {
                  borderBottom:
                    'var(--b-border-width-s) solid var(--input_border_error_1)'
                },
                '& .MuiInputBase-root.Mui-focused': {
                  borderBottom:
                    'var(--b-border-width-s) solid var(--input_border_focus_1)'
                },

                '& .MuiInputBase-input::placeholder': {
                  opacity: 1
                },

                '& .MuiInputAdornment-root': {
                  display: 'none'
                }
              }
            }
          }}
          className={inputClassName}
          disabled={disabled}
          shouldDisableDate={(day) =>
            day ? shouldDisableDate(day.toJSDate()) : false
          }
          {...props}
        />
      </Box>
    </Box>
  );
};
